import { c as _c } from "react/compiler-runtime";
import { IcoImages24 } from '@onefootprint/icons';
import { useToast } from '@onefootprint/ui';
import type React from 'react';
import { useRef, useState } from 'react';
import { Logger } from '@/idv2/utils/logger';
import useProcessImage, { isProcessingResultError } from '../../../../hooks/use-process-image';
import type { ReceivedImagePayload } from '../../../../types';
type Callbacks = 'onUploadSuccess' | 'onUploadChangeDone' | 'onUploadBtnClick';
export type UploadButtonCallbacks = Pick<UploadButtonProps, Callbacks>;
export type UploadButtonProps = {
  onUploadBtnClick: () => void;
  onUploadChangeDone: () => void;
  onUploadSuccess: (payload: ReceivedImagePayload) => void;
  allowPdf: boolean;
  hasBadConnectivity?: boolean;
};
const logWarn = (e: string) => Logger.warn(e, {
  location: 'upload-button'
});
const logProcessedFile = (res: {
  file: File | Blob;
  extraCompressed: boolean;
}) => {
  Logger.info(`UploadButton: size of the processed file to be sent in machine event type 'receivedImage' is ${res.file.size}, file type ${res.file.type}`);
};
const UploadButton = t0 => {
  const $ = _c(23);
  const {
    onUploadBtnClick,
    onUploadChangeDone,
    onUploadSuccess,
    allowPdf,
    hasBadConnectivity
  } = t0;
  const uploadPhotoRef = useRef(undefined);
  const [isLoading, setIsLoading] = useState(false);
  let t1;
  if ($[0] !== allowPdf) {
    t1 = {
      allowPdf
    };
    $[0] = allowPdf;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    processImageFile,
    acceptedFileFormats
  } = useProcessImage(t1);
  const {
    toast
  } = useToast();
  let t2;
  if ($[2] !== onUploadChangeDone) {
    t2 = () => {
      setIsLoading(false);
      onUploadChangeDone();
    };
    $[2] = onUploadChangeDone;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const onProcessingDone = t2;
  let t3;
  if ($[4] !== hasBadConnectivity || $[5] !== onProcessingDone || $[6] !== onUploadBtnClick || $[7] !== onUploadSuccess || $[8] !== processImageFile || $[9] !== toast) {
    t3 = async event => {
      setIsLoading(true);
      onUploadBtnClick();
      const {
        files
      } = event.target;
      if (!files?.length) {
        onProcessingDone();
        return;
      }
      if (!files[0].type.startsWith("image")) {
        logWarn("Uploaded file is not an image");
        toast({
          title: "Uh-oh",
          description: "Only image files are supported. Please try again."
        });
        onProcessingDone();
        return;
      }
      const processResult = await processImageFile(files[0], hasBadConnectivity);
      if (isProcessingResultError(processResult)) {
        logWarn("Captured image could not be processed - retaking the image");
        onProcessingDone();
        return;
      }
      logProcessedFile(processResult);
      onUploadSuccess({
        captureKind: "upload",
        extraCompressed: processResult.extraCompressed,
        imageFile: processResult.file,
        forcedUpload: false
      });
      onProcessingDone();
    };
    $[4] = hasBadConnectivity;
    $[5] = onProcessingDone;
    $[6] = onUploadBtnClick;
    $[7] = onUploadSuccess;
    $[8] = processImageFile;
    $[9] = toast;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const handleImageChange = t3;
  let t4;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => uploadPhotoRef.current?.click();
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const handleUploadClick = t4;
  let t5;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <IcoImages24 className="text-white" />;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== isLoading) {
    t6 = <button type="button" onClick={handleUploadClick} disabled={isLoading} className="flex justify-center items-center bg-black/20 border-none rounded-full absolute bottom-6 left-4 shadow-md cursor-pointer w-14 h-14" data-dd-action-name="doc:upload-photo">{t5}</button>;
    $[13] = isLoading;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== acceptedFileFormats) {
    t7 = acceptedFileFormats.join(",");
    $[15] = acceptedFileFormats;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== handleImageChange || $[18] !== t7) {
    t8 = <input ref={uploadPhotoRef as React.RefObject<HTMLInputElement | null>} type="file" accept={t7} onChange={handleImageChange} className="hidden" />;
    $[17] = handleImageChange;
    $[18] = t7;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== t6 || $[21] !== t8) {
    t9 = <>{t6}{t8}</>;
    $[20] = t6;
    $[21] = t8;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  return t9;
};
export default UploadButton;